import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, Sparkles, AlertTriangle, CreditCard, Loader2, ExternalLink,
  ShieldCheck, RefreshCcw, History, XCircle, Clock, CalendarClock,
} from "lucide-react";import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatCHF, formatDateTime, DELIVERY_TYPE_LABEL, DELIVERY_TYPE_DESC } from "@/lib/format";
import CancelSubscriptionModal from "@/components/CancelSubscriptionModal";
import CreditRestoredBanner from "@/components/CreditRestoredBanner";
import OutOfRidesBlock from "@/components/OutOfRidesBlock";
import DeliveryPacksCard from "@/components/DeliveryPacksCard";
import PackOnboardingModal from "@/components/PackOnboardingModal";
import PackLowBalanceBanner from "@/components/PackLowBalanceBanner";
import { useCheckoutReturn } from "@/lib/useCheckoutReturn";
import CheckoutVerification from "@/components/CheckoutVerification";
import { trackEvent } from "@/lib/analytics";
import { isBusinessCustomer, validPriceTax } from "@/lib/priceTax";
import CustomerTaxBreakdown from "@/components/CustomerTaxBreakdown";

const FEATURED = "business_pro";

const STATUS_PILL = {
  active:                { label: "Attivo",            tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trialing:              { label: "In prova",          tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  simulated:             { label: "Prova simulata",     tone: "bg-amber-50 text-amber-800 border-amber-200" },
  past_due:              { label: "Pagamento in ritardo", tone: "bg-red-50 text-red-700 border-red-200" },
  scheduled_to_cancel:   { label: "Cancellazione programmata", tone: "bg-amber-50 text-amber-800 border-amber-200" },
  pending:               { label: "In attesa pagamento", tone: "bg-amber-50 text-amber-800 border-amber-200" },
  incomplete:            { label: "Incompleto",        tone: "bg-amber-50 text-amber-800 border-amber-200" },
  canceled:              { label: "Annullato",         tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  cancelled:             { label: "Annullato",         tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  expired:               { label: "Scaduto",           tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};

function StatusPill({ value }) {
  // Phase 43.20 — Default a "pending" (sicuro). Stati ignoti/non riconosciuti
  // non devono apparire come "Attivo". Le prove simulate restano riconoscibili.
  const meta = STATUS_PILL[value] || STATUS_PILL.pending;
  return (
    <span
      data-testid="subscription-status-pill"
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${meta.tone}`}
    >
      {meta.label}
    </span>
  );
}

export default function Subscription() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [packCatalog, setPackCatalog] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [card, setCard] = useState({ has_card: false, cards: [] });
  const [extras, setExtras] = useState({ items: [], total: 0, total_succeeded_chf: 0, total_simulated_chf: 0, total_failed_chf: 0, account_status: "simulated" });
  const [busy, setBusy] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  // Phase 43.56 — Track unique "viewed" events una sola volta per session
  const viewedRef = useRef({ outOfRides: false, creditBanner: false });
  // Phase 44.06 — Pack onboarding micro-modal
  const [showPackOnboarding, setShowPackOnboarding] = useState(false);
  const { user: authUser, refresh: refreshUser } = useAuth();
  const business = isBusinessCustomer(authUser?.role);
  useEffect(() => {
    // Trigger: utente ha almeno 1 corsa nel pack E non ha ancora ack-ato l'onboarding
    if (!authUser || authUser.pack_onboarding_acked_at) return;
    const remaining = me?.packs?.total_remaining || 0;
    if (remaining > 0) {
      setShowPackOnboarding(true);
    }
  }, [authUser, me?.packs?.total_remaining]);
  // Quando il modal si chiude (ack), refresh user per aggiornare il flag
  const closePackOnboarding = async () => {
    setShowPackOnboarding(false);
    try { await refreshUser?.(); } catch { /* */ }
  };

  const load = async () => {
    setLoadError("");
    try {
      const [{ data: p }, { data: m }, catalogResponse, configResponse] = await Promise.all([
        api.get("/subscriptions/plans", { params: { audience: business ? "business" : "private" } }),
        api.get("/subscriptions/me"),
        api.get("/subscriptions/packs/catalog", { params: { audience: business ? "business" : "private" } }).catch(() => null),
        api.get("/public/config").catch(() => null),
      ]);
      setPlans(p.plans);
      setMe(m);
      setPackCatalog(catalogResponse?.data?.items?.find(item => item.id === "pack_10") || null);
      setPaymentConfig(configResponse?.data || null);
      try {
        const { data: h } = await api.get("/subscriptions/history");
        setHistory(h.items || []);
      } catch { /* ignore */ }
      try {
        const { data: c } = await api.get("/subscriptions/saved-card");
        setCard(c);
      } catch { /* ignore */ }
      try {
        const { data: e } = await api.get("/subscriptions/extras");
        setExtras(e);
      } catch { /* ignore */ }
    } catch (error) {
      setLoadError(formatApiError(error.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [business]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearReturnParams = keys => setParams(previous => {
    const next = new URLSearchParams(previous);
    keys.forEach(key => next.delete(key));
    return next;
  }, { replace: true });
  const stripeSession = params.get("stripe_session");
  const packSession = params.get("pack_session");
  const cancelled = params.get("cancelled");
  const packCancelled = params.get("pack_cancelled");
  useEffect(() => {
    if (cancelled) { toast.info("Checkout annullato."); clearReturnParams(["cancelled"]); }
    if (packCancelled) { toast.info("Acquisto pacchetto annullato."); clearReturnParams(["pack_cancelled"]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelled, packCancelled]);
  const subscriptionVerification = useCheckoutReturn({
    sessionId: stripeSession,
    request: async () => (await api.post("/subscriptions/refresh", { session_id: stripeSession })).data,
    isComplete: data => ["active", "trialing"].includes(data.stripe_status),
    isTerminal: data => ["expired", "canceled", "cancelled", "failed"].includes(data.stripe_status),
    onComplete: data => {
      toast.success(`Abbonamento attivato: ${data.plan}`);
      trackEvent("subscription_upgrade_completed", { plan: data.plan, stripe_status: data.stripe_status });
      clearReturnParams(["stripe_session", "plan"]);
      load();
      Promise.resolve(refresh()).catch(() => {});
    },
    interval: 2000,
  });
  const packVerification = useCheckoutReturn({
    sessionId: packSession,
    request: async () => (await api.post("/subscriptions/packs/refresh", { session_id: packSession })).data,
    isComplete: data => data.status === "active",
    isTerminal: data => [data.status, data.stripe_status].some(status => ["expired", "canceled", "cancelled", "failed"].includes(status)),
    onComplete: data => {
      toast.success("Pacchetto +10 corse attivato!");
      trackEvent("subscription_pack_purchased", { pack_id: data.pack?.id, catalog_id: data.pack?.catalog_id, phase: "activated" });
      clearReturnParams(["pack_session"]);
      load();
    },
    interval: 2000,
  });
  const verificationPending = subscriptionVerification.unresolved || packVerification.unresolved;

  const startCheckout = async (planId) => {
    if (!paymentConfig?.payments_available) { toast.info("Le nuove attivazioni a pagamento non sono ancora disponibili."); return; }
    if (!business) return;
    const offer = plans.find(plan => plan.id === planId);
    if (!validPriceTax(offer?.price_tax) || offer.price_tax.audience !== "business") {
      toast.error("Prezzo IVA inclusa non disponibile. Ricarica la pagina prima di procedere.");
      return;
    }
    if (verificationPending) { toast.info("Verifica prima il pagamento precedente."); return; }
    setBusy(planId);
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan: planId });
      if (!data?.url) {
        // Phase 43.20 — no fallback: se non c'è URL Stripe valido è errore
        toast.error("Impossibile aprire il pagamento. Riprova o contatta il supporto.");
        return;
      }
      // Redirect to Stripe Checkout (real test mode)
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  // Phase 43.58 — Pack checkout one-shot (Stripe)
  const startPackCheckout = async () => {
    if (!paymentConfig?.payments_available) { toast.info("L'acquisto del pacchetto non è ancora disponibile."); return; }
    const tax = validPriceTax(packCatalog?.price_tax) ? packCatalog.price_tax : null;
    if (!packCatalog || (business ? tax?.audience !== "business" : (tax && tax.audience !== "private"))) {
      toast.error("Prezzo del pacchetto non disponibile. Ricarica la pagina prima di procedere.");
      return;
    }
    if (verificationPending) { toast.info("Verifica prima il pagamento precedente."); return; }
    setBusy("pack");
    try {
      const { data } = await api.post("/subscriptions/packs/checkout", { catalog_id: "pack_10" });
      if (!data?.checkout_url) {
        toast.error("Impossibile aprire il pagamento del pacchetto.");
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };


  const subscribeFree = async () => {
    setBusy("free");
    try {
      const { data } = await api.post("/subscriptions/subscribe", { plan: "free" });
      if (data?.scheduled_cancel) {
        toast.success("Disdetta programmata a fine periodo. Le consegne incluse restano disponibili fino alla scadenza.");
      } else {
        toast.success("Tornato a Free");
      }
      setCancelModalOpen(false);
      await load();
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  // Phase 43.21 — quando l'utente sta passando da un piano paid → free,
  // mostra SEMPRE la modale di conferma con info su grace period e consegne residue.
  const handleSwitchToFree = () => {
    if (me?.plan?.id && me.plan.id !== "free" && me.active) {
      setCancelModalOpen(true);
      return;
    }
    subscribeFree();
  };

  const cancelSub = async () => {
    setBusy("cancel");
    try {
      const { data } = await api.post("/subscriptions/cancel");
      if (data.scheduled_cancel) {
        toast.success("Disdetta programmata a fine periodo.");
      } else {
        toast.success("Tornato a Free");
      }
      setCancelModalOpen(false);
      await load();
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const reactivateSub = async () => {
    setBusy("reactivate");
    try {
      await api.post("/subscriptions/reactivate");
      toast.success("Rinnovo riattivato.");
      await load();
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/subscriptions/portal");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(null);
    }
  };

  if (loadError && !me) return <div className="card-soft p-5 space-y-3" role="alert"><p>{loadError}</p><button className="btn-ghost" onClick={load}>Riprova</button></div>;
  if (!plans.length || !me) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;
  const current = me.plan.id;
  const offeredPlans = business ? plans : plans.filter(plan => plan.id === "free");
  const currentTax = validPriceTax(me.plan.price_tax) ? me.plan.price_tax : null;
  const hasPaidPlan = me.active && current !== "free";
  const hasLegacyPlan = hasPaidPlan && !currentTax;
  const usagePct = me.deliveries_used_pct || 0;
  const includedRemaining = Math.max(0, (me.included_deliveries_monthly || 0) - (me.included_deliveries_used || 0));
  const paymentStatus = me.payment_status || "pending";
  const isStripeBacked = paymentStatus !== "pending" && paymentStatus !== "incomplete" && paymentStatus !== "simulated";
  const scheduledCancel = Boolean(me.cancel_at_period_end);
  const effectiveEnd = me.effective_end_date || me.current_period_end || me.billing_cycle_end;

  // Phase 43.55 — Out-of-rides logic: solo se il cliente ha consumato tutte le incluse su un piano paid attivo.
  const outOfRides = me.active && current !== "free" && (me.included_deliveries_monthly || 0) > 0 && includedRemaining === 0;
  // Upgrade suggerito = piano successivo nella lista (più alto del corrente)
  const upgradePlan = (() => {
    if (!business) return null;
    const idx = plans.findIndex((p) => p.id === current);
    if (idx < 0) return null;
    for (let i = idx + 1; i < plans.length; i++) {
      if ((plans[i].included_deliveries_monthly || 0) > (plans[idx].included_deliveries_monthly || 0)) {
        return plans[i];
      }
    }
    return null;
  })();
  // Phase 43.55 — banner credit restored
  const recentCredits = me.recent_credits_restored || [];
  const handleDismissCredits = () => {
    trackEvent("credit_restored_banner_dismissed", { count: recentCredits.length });
    // Reset locale dei recent_credits → al prossimo refresh il banner non torna
    setMe({ ...me, recent_credits_restored: [] });
  };

  // Phase 43.56 — Track "viewed" una volta per render-session
  if (outOfRides && !viewedRef.current.outOfRides) {
    viewedRef.current.outOfRides = true;
    trackEvent("out_of_rides_block_viewed", {
      current_plan: current,
      suggested_plan: upgradePlan?.id,
    });
  }
  if (recentCredits.length > 0 && !viewedRef.current.creditBanner) {
    viewedRef.current.creditBanner = true;
    trackEvent("credit_restored_banner_viewed", { count: recentCredits.length });
  }

  const scrollToPlans = () => {
    const el = document.querySelector('[data-testid="plans-grid"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-6" data-testid="customer-subscription-page">
      <div>
        <p className="overline">Il mio piano</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">{business ? "Abbonamenti DapNow Business" : "Il tuo piano e i pacchetti"}</h1>
        <p className="text-dapnow-muted mt-2 text-sm max-w-2xl">
          {business
            ? "Scegli un canone mensile con le consegne previste dal piano oppure un pacchetto senza rinnovo. Le nuove offerte mostrano imponibile, IVA e totale da pagare. Le condizioni del piano restano distinte dalla disponibilità dei servizi."
            : "Account gratuito e consegne singole, oppure il pacchetto da 10 corse con pagamento una tantum e nessun rinnovo automatico."}
        </p>
      </div>

      <div className="rounded-2xl border border-dapnow-line bg-dapnow-bg p-4 text-sm" data-testid="subscription-service-availability">
        <p><strong>Standard:</strong> {DELIVERY_TYPE_DESC.standard}. Per ritiri tra le 18:00 e le 21:00, contatta DapNow per concordare i tempi prima di prenotare.</p>
        <p className="mt-2"><strong>Shared:</strong> {DELIVERY_TYPE_DESC.shared}. <Link to="/become-driver" className="font-semibold text-dapnow-blue underline">Richiedi informazioni</Link>.</p>
        <p className="mt-2"><strong>Express:</strong> entro 120 minuti dal ritiro, dopo la conferma operativa di tratta e disponibilità da parte del team. Il team verifica che la consegna possa concludersi entro le 21:00, nella fascia di servizio 06:30–21:00. Il preventivo non è una prenotazione accettata.</p>
        <p className="mt-2"><strong>Van:</strong> su richiesta, con verifica preventiva. Massimo 25 kg e 120 × 80 × 80 cm per collo, imballaggio incluso; prezzo dedicato, non incluso.</p>
        {hasLegacyPlan && <p className="mt-2 text-xs text-dapnow-muted">I diritti, i residui e lo storico del tuo abbonamento non cambiano. Un servizio previsto dal piano può non essere ancora disponibile per nuove prenotazioni.</p>}
      </div>

      <CheckoutVerification verification={subscriptionVerification} testId="subscription-verification" />
      <CheckoutVerification verification={packVerification} testId="pack-verification" />

      {/* Phase 43.55 — Banner +1 corsa rimborsata (sopra al polling/active card) */}
      {recentCredits.length > 0 && (
        <CreditRestoredBanner
          credits={recentCredits}
          onDismiss={handleDismissCredits}
        />
      )}

      {/* Phase 43.55/59 — Blocco "Corse esaurite" — Pack-first */}
      {outOfRides && (
        <OutOfRidesBlock
          currentPlan={me.plan}
          upgradePlan={upgradePlan}
          packPriceChf={packCatalog?.price_chf ?? null}
          packCatalog={packCatalog}
          business={business}
          paymentsDisabled={!paymentConfig?.payments_available}
          packBusy={busy === "pack" || verificationPending || !paymentConfig?.payments_available}
          onUpgrade={() => {
            trackEvent("out_of_rides_cta_upgrade", {
              current_plan: current,
              suggested_plan: upgradePlan?.id,
              suggested_price_chf: upgradePlan?.price_chf,
            });
            if (upgradePlan) startCheckout(upgradePlan.id);
            else scrollToPlans();
          }}
          onBuyPack={() => {
            trackEvent("out_of_rides_cta_pack", {
              current_plan: current,
              pack_price_chf: packCatalog?.price_chf ?? null,
            });
            startPackCheckout();
          }}
          onSingleRide={() => {
            trackEvent("out_of_rides_cta_single", { current_plan: current });
            navigate("/app/new-order");
          }}
        />
      )}

      {/* Phase 44.06 — Low balance banner (≤2 corse) */}
      <PackLowBalanceBanner
        totalRemaining={me.packs?.total_remaining}
        onBuyPack={() => document.querySelector('[data-testid="delivery-packs-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
      />

      {/* Phase 43.58 — Card Credito Consegne (Pacchetto extra +10 corse) */}
      <DeliveryPacksCard
        packs={me.packs}
        catalog={packCatalog}
        business={business}
        busy={busy === "pack" || verificationPending}
        disabled={!paymentConfig?.payments_available}
        onBuyPack={startPackCheckout}
      />

      {/* Phase 44.06 — Micro-onboarding alla prima volta che si vede il pack */}
      <PackOnboardingModal
        open={showPackOnboarding}
        onClose={closePackOnboarding}
        totalRemaining={me.packs?.total_remaining || 10}
      />

      {/* Active plan card */}
      {me.active && (
        <section className="card-soft p-5 sm:p-7 space-y-5" data-testid="subscription-usage">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="overline">Piano attivo</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <h2 className="font-display text-2xl font-bold tracking-tight">{me.plan.name}</h2>
                <StatusPill value={scheduledCancel ? "scheduled_to_cancel" : paymentStatus} />
              </div>
              <p className="text-xs text-dapnow-muted mt-1" data-testid="subscription-period-line">
                {formatCHF(currentTax?.total_chf ?? me.plan.price_chf)}/mese{currentTax ? " IVA inclusa" : " · importo del piano esistente"} · {scheduledCancel ? "Attivo fino al" : "ciclo fino al"}{" "}
                <strong>{effectiveEnd ? new Date(effectiveEnd).toLocaleDateString("it-CH") : "—"}</strong>
              </p>
              {currentTax && <CustomerTaxBreakdown tax={currentTax} className="mt-3" testId="current-plan-tax" />}
            </div>
            <span className={`pill ${alertTone(me.alert_level)}`}>{alertLabel(me.alert_level)}</span>
          </div>

          {scheduledCancel && (
            <div
              data-testid="scheduled-cancel-banner"
              className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 text-sm text-amber-900"
            >
              <CalendarClock className="size-5 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">Disdetta programmata</p>
                <p className="text-xs mt-1">
                  Il tuo piano <strong>{me.plan.name}</strong> resterà attivo fino al{" "}
                  <strong>{effectiveEnd ? new Date(effectiveEnd).toLocaleDateString("it-CH") : "—"}</strong>.
                  Dopo questa data passerai automaticamente al piano <strong>Free</strong>.
                  Fino ad allora puoi continuare a usare le{" "}
                  <strong>{includedRemaining}</strong> consegne incluse residue.
                </p>
                <button
                  onClick={reactivateSub}
                  disabled={busy === "reactivate"}
                  data-testid="reactivate-subscription-btn"
                  className="mt-3 inline-flex items-center gap-2 rounded-xl bg-dapnow-blue text-white px-4 py-2 text-xs font-semibold hover:bg-dapnow-blue-hover disabled:opacity-60"
                >
                  {busy === "reactivate" ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCcw className="size-3.5" />}
                  Riattiva rinnovo
                </button>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-5">
            <UsageBar testid="usage-deliveries" label="Consegne incluse usate" used={me.included_deliveries_used || 0} total={me.included_deliveries_monthly || 0} pct={usagePct} />
            <div data-testid="usage-included-summary" className="rounded-2xl border border-dapnow-line bg-dapnow-bg p-4">
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium tracking-tight">Residue questo mese</span>
                <span className="font-display text-2xl font-extrabold tabular-nums text-dapnow-blue" data-testid="included-remaining-count">{includedRemaining}</span>
              </div>
              <p className="text-xs text-dapnow-muted mt-1">
                {me.included_km_per_delivery ? `Fino a ${me.included_km_per_delivery} km cad.` : ""}
                {me.eligible_delivery_types?.length ? ` · Servizi previsti dal piano: ${me.eligible_delivery_types.map((t) => t === "shared" ? "Shared (in arrivo)" : (DELIVERY_TYPE_LABEL[t] || t)).join(" + ")}` : ""}
              </p>
              <p className="text-[11px] text-dapnow-muted mt-2 italic">
                Oltre {me.included_km_per_delivery || 10} km: solo i km extra fatturati. Express: entro 120 minuti dal ritiro dopo conferma operativa, con consegna entro le 21:00 e supplemento secondo il piano. Van: su richiesta, con verifica preventiva e prezzo dedicato.
              </p>
            </div>
          </div>

          {me.alert_level !== "ok" && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 text-sm text-amber-900">
              <AlertTriangle className="h-5 w-5 mt-0.5" />
              <div>
                <p className="font-semibold">{alertHeading(me.alert_level)}</p>
                <p className="text-xs">Le consegne oltre la soglia saranno alla tariffa standard. {business ? "Puoi valutare un piano con più consegne incluse o un pacchetto." : "Puoi acquistare un pacchetto senza rinnovo."}</p>
              </div>
            </div>
          )}

          {/* Manage actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-dapnow-line">
            {isStripeBacked && (
              <button
                onClick={openPortal}
                disabled={busy === "portal"}
                data-testid="manage-billing-btn"
                className="btn-ghost"
              >
                {busy === "portal" ? <Loader2 className="size-4 animate-spin" /> : <CreditCard className="size-4" />}
                Gestisci fatturazione
                <ExternalLink className="size-3" />
              </button>
            )}
            {!scheduledCancel && (
              <button
                onClick={() => setCancelModalOpen(true)}
                disabled={busy === "cancel"}
                data-testid="cancel-subscription-btn"
                className="btn-ghost text-dapnow-red border-dapnow-red/40"
              >
                {busy === "cancel" ? <Loader2 className="size-4 animate-spin" /> : <XCircle className="size-4" />}
                Disdici a fine periodo
              </button>
            )}
          </div>
        </section>
      )}

      {/* Saved card + extras this cycle (visible once subscription exists) */}
      {me.active && (
        <section className="grid md:grid-cols-2 gap-4" data-testid="saved-card-and-extras">
          <div className="card-soft p-5" data-testid="saved-card-panel">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="size-4 text-dapnow-muted" />
              <h2 className="font-display text-lg font-bold tracking-tight">Metodo di pagamento</h2>
            </div>
            {card.has_card && card.cards.length > 0 ? (
              <div className="space-y-2">
                {card.cards.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-xl bg-dapnow-bg px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-8 h-5 rounded bg-white text-[10px] font-black text-dapnow-ink border border-zinc-200 uppercase">{c.brand || "CARD"}</span>
                      <span className="font-mono text-sm">•••• {c.last4}</span>
                    </div>
                    <span className="text-[11px] text-dapnow-muted">{String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span>
                  </div>
                ))}
                <p className="text-[11px] text-dapnow-muted italic mt-2">
                  Gli extra previsti dal piano per i servizi disponibili, i km aggiuntivi e le consegne oltre soglia vengono addebitati automaticamente su questa carta. Questa indicazione non rende disponibili Shared, Express o fasce orarie non confermate.
                </p>
              </div>
            ) : (
              <div className="text-sm text-dapnow-muted">
                Nessuna carta salvata.
                {isStripeBacked ? " Usa il Billing Portal per aggiungerne una." : " Passa a una sottoscrizione reale per salvarla."}
              </div>
            )}
          </div>
          <div className="card-soft p-5" data-testid="extras-panel">
            <div className="flex items-center gap-2 mb-2">
              <History className="size-4 text-dapnow-muted" />
              <h2 className="font-display text-lg font-bold tracking-tight">Extra di questo ciclo</h2>
              {extras.account_status === "past_due" && (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 text-red-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                  <AlertTriangle className="size-3" /> Extra non saldati
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Tile label="Addebitati" value={formatCHF(extras.total_succeeded_chf)} tone="text-emerald-700" />
              <Tile label="Falliti" value={formatCHF(extras.total_failed_chf)} tone="text-red-700" />
            </div>
            {extras.items.length === 0 ? (
              <p className="text-xs text-dapnow-muted italic">Nessun extra in questo ciclo.</p>
            ) : (
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-dapnow-line">
                    {extras.items.filter((it) => it.status !== "simulated").slice(0, 10).map((it, i) => (
                      <tr key={it.id || i}>
                        <td className="py-1.5 pr-2 text-dapnow-muted whitespace-nowrap">{formatDateTime(it.created_at)}</td>
                        <td className="py-1.5 pr-2 truncate">{it.description}</td>
                        <td className="py-1.5 text-right font-semibold whitespace-nowrap">{formatCHF(it.amount_chf)}</td>
                        <td className="py-1.5 pl-2 text-right">
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
                            it.status === "succeeded" ? "bg-emerald-100 text-emerald-800" :
                            it.status === "failed" ? "bg-red-100 text-red-800" :
                            "bg-zinc-100 text-zinc-700"
                          }`}>{it.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Terms banner — shown above plans grid */}
      {(business || (me.active && current !== "free")) && <div className="rounded-2xl border border-dapnow-blue/20 bg-dapnow-blue-soft/60 px-4 py-3 text-xs text-dapnow-ink leading-relaxed" data-testid="subscription-terms">
        <strong>Termini:</strong> il piano include un numero di consegne locali fino a 10 km. Gli extra previsti dalle condizioni del piano per servizi effettivamente disponibili saranno <strong>addebitati automaticamente</strong> sulla carta registrata al termine di ogni ordine. La disponibilità dei servizi e degli orari va confermata prima della prenotazione. Il canone mensile è anticipato e si rinnova automaticamente fino alla cancellazione.
      </div>}

      {/* Plans grid */}
      <div className={`grid gap-4 ${business ? "md:grid-cols-2 xl:grid-cols-3" : "max-w-xl"}`} data-testid="plans-grid">
        {offeredPlans.map((p) => {
          const isCurrent = current === p.id;
          const featured = p.id === FEATURED;
          const tax = validPriceTax(p.price_tax) ? p.price_tax : null;
          const offerAvailable = p.price_chf === 0 || (tax?.audience === "business");
          const isUpgrade = !isCurrent && currentRank(plans, current) < currentRank(plans, p.id);
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-3xl p-6 border ${featured ? "bg-dapnow-ink text-white border-dapnow-ink shadow-[0_30px_60px_rgba(0,82,255,0.18)]" : "bg-white border-dapnow-line card-soft"}`}
            >
              {featured && <span className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-dapnow-blue text-white text-xs font-semibold rounded-full px-3 py-1"><Sparkles className="h-3.5 w-3.5" /> Più popolare</span>}
              <p className={`overline ${featured ? "text-white/60" : ""}`}>{p.name}</p>
              <p className="font-display text-3xl font-extrabold tracking-tighter mt-2">
                {p.price_chf === 0 ? "CHF 0" : `${formatCHF(p.price_chf)}`}
                <span className={`text-sm font-semibold ${featured ? "text-white/70" : "text-dapnow-muted"}`}>/mese</span>
              </p>
              {business && p.price_chf > 0 && <p className="text-xs mt-1">IVA esclusa</p>}
              {business && tax && p.price_chf > 0 && <CustomerTaxBreakdown tax={tax} className="mt-3" testId={`plan-tax-${p.id}`} totalLabel="Totale mensile IVA inclusa" />}
              {!offerAvailable && <p className="text-xs mt-2">Prezzo completo temporaneamente non disponibile.</p>}
              {p.target && <p className={`text-xs mt-1 ${featured ? "text-white/60" : "text-dapnow-muted"}`}>{p.target}</p>}
              {p.included_deliveries_monthly > 0 ? (
                <div className={`mt-4 rounded-xl px-3 py-2 text-xs ${featured ? "bg-white/10" : "bg-dapnow-bg"}`}>
                  <strong>{p.included_deliveries_monthly}</strong> consegne locali incluse/mese · fino a <strong>{p.included_km_per_delivery} km</strong> cad. · Standard; Shared previsto dal piano, in arrivo e non prenotabile
                </div>
              ) : null}
              <ul className={`mt-5 space-y-2 text-sm ${featured ? "text-white/90" : ""}`}>
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-dapnow-blue" /> <span>{perk === "Servizi inclusi: Shared e Standard" ? "Servizi previsti dal piano: Standard e Shared (in arrivo, non prenotabile)" : perk}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {isCurrent ? (
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${featured ? "bg-white/15 text-white" : "bg-emerald-50 text-emerald-700"}`}>
                    <Check className="h-3.5 w-3.5" /> Piano attivo
                  </span>
                ) : (
                  <button
                    onClick={() => p.id === "free" ? handleSwitchToFree() : startCheckout(p.id)}
                    disabled={busy === p.id || busy === "free" || verificationPending || !offerAvailable || (p.price_chf > 0 && !paymentConfig?.payments_available)}
                    data-testid={`plan-subscribe-${p.id}`}
                    className={`w-full min-h-12 py-3 rounded-xl font-semibold tracking-tight transition flex flex-wrap items-center justify-center gap-2 ${featured ? "bg-dapnow-blue text-white hover:bg-dapnow-blue-hover" : "btn-ghost"}`}
                  >
                    {busy === p.id ? (<><Loader2 className="size-4 animate-spin" /> Avvio…</>) :
                     p.price_chf === 0 ? (me?.plan?.id && me.plan.id !== "free" && me.active ? "Disdici e passa a Free" : "Torna a Free") :
                     (<><CreditCard className="size-4" /> {isUpgrade ? "Fai upgrade" : "Attiva"} · {offerAvailable ? `${formatCHF(tax.total_chf)}/mese` : "prezzo da verificare"}</>)}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Inclusion rules disclaimer */}
      {(business || hasPaidPlan) ? <div
        data-testid="inclusion-rules"
        className="rounded-2xl border border-dapnow-line bg-dapnow-bg/60 p-4 text-xs text-dapnow-muted leading-relaxed"
      >
        <p className="font-semibold text-dapnow-ink mb-1">Come funzionano le consegne incluse</p>
        <ul className="space-y-0.5 list-disc pl-4">
          <li>Ogni consegna inclusa copre la tariffa <strong>fino a 10 km</strong> per i servizi previsti dal piano. <strong>Standard</strong> è il servizio con flotta interna; <strong>Shared</strong> è in arrivo e non ancora prenotabile.</li>
          <li>Se la distanza supera i 10 km, il cliente paga <strong>solo i km extra</strong>.</li>
          <li><strong>Express</strong>: entro 120 minuti dal ritiro dopo la conferma operativa del team su tratta e disponibilità, con consegna entro le 21:00. Inclusioni e supplementi seguono le condizioni del piano; il preventivo non accetta la prenotazione.</li>
          <li><strong>Van</strong>: su richiesta, con verifica preventiva; massimo 25 kg e 120 × 80 × 80 cm per collo, imballaggio incluso. Non incluso, prezzo dedicato.</li>
          <li>Oltre il numero mensile incluso le consegne sono fatturate alla tariffa standard.</li>
        </ul>
      </div> : <p className="text-xs text-dapnow-muted" data-testid="private-pack-conditions">Il pacchetto copre le consegne Standard nell’area servita. Express e Van richiedono una verifica preventiva e non sono inclusi nel pacchetto.</p>}

      {/* History */}
      {history.length > 0 && (
        <section className="card-soft p-5 sm:p-6" data-testid="subscription-history">
          <div className="flex items-center gap-2 mb-3">
            <History className="size-4 text-dapnow-muted" />
            <h2 className="font-display text-lg font-bold tracking-tight">Storico abbonamento</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-dapnow-muted">
                <tr>
                  <th className="text-left py-2 pr-3">Data</th>
                  <th className="text-left py-2 pr-3">Evento</th>
                  <th className="text-left py-2 pr-3">Da</th>
                  <th className="text-left py-2">A</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dapnow-line">
                {history.map((h, idx) => (
                  <tr key={h.id || idx} data-testid={`history-row-${idx}`}>
                    <td className="py-2 pr-3 text-xs text-dapnow-muted whitespace-nowrap">{formatDateTime(h.changed_at)}</td>
                    <td className="py-2 pr-3 text-xs">
                      {h.event_type ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dapnow-bg text-dapnow-ink text-[11px]">
                          {h.event_type.replace(/^customer\./, "").replace(/^invoice\./, "fattura.").replaceAll("_", " ")}
                        </span>
                      ) : <span className="text-dapnow-muted">manuale</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{h.from_plan || "—"}</td>
                    <td className="py-2 font-mono text-xs font-semibold">{h.to_plan || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="flex items-start gap-2 text-xs text-dapnow-muted">
        <ShieldCheck className="size-4 shrink-0 mt-0.5" />
        <p data-testid="subscription-payment-mode">
          {paymentConfig?.payments_available && paymentConfig?.stripe_mode === "live"
            ? "Pagamento gestito da Stripe. Controlla importo e condizioni nel riepilogo prima di confermare."
            : paymentConfig?.payments_available && paymentConfig?.stripe_mode === "test"
            ? "Ambiente di collaudo Stripe: i pagamenti sono di prova e non acquistano un servizio reale."
            : paymentConfig?.simulation_enabled
            ? "Ambiente di collaudo: le operazioni simulate non rappresentano pagamenti reali."
            : "Pagamenti in preparazione. Le nuove attivazioni a pagamento non sono ancora disponibili."}
        </p>
      </div>

      <button onClick={() => navigate("/app/profile")} className="text-sm font-semibold text-dapnow-blue">← Torna al profilo</button>

      {cancelModalOpen && (
        <CancelSubscriptionModal
          me={me}
          busy={busy === "cancel"}
          onConfirm={cancelSub}
          onClose={() => { if (busy !== "cancel") setCancelModalOpen(false); }}
        />
      )}
    </div>
  );
}

function UsageBar({ testid, label, used, total, pct, hint }) {  const tone = pct >= 100 ? "bg-dapnow-red" : pct >= 90 ? "bg-amber-500" : pct >= 80 ? "bg-amber-400" : "bg-dapnow-blue";
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium tracking-tight">{label}</span>
        <span className="font-display font-semibold">{total ? `${used} / ${total}` : used}</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-dapnow-line overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {hint && <p className="text-xs text-dapnow-muted mt-1">{hint}</p>}
    </div>
  );
}

function alertTone(lvl) {
  if (lvl === "limit_reached") return "bg-red-50 text-red-700";
  if (lvl === "near_limit_90") return "bg-amber-50 text-amber-700";
  if (lvl === "near_limit_80") return "bg-amber-50 text-amber-700";
  return "bg-emerald-50 text-emerald-700";
}
function alertLabel(lvl) {
  return { ok: "OK", near_limit_80: "80%", near_limit_90: "90%", limit_reached: "Soglia raggiunta" }[lvl] || lvl;
}
function alertHeading(lvl) {
  return { near_limit_80: "Hai usato l'80% delle consegne incluse", near_limit_90: "Hai usato il 90% delle consegne incluse", limit_reached: "Soglia mensile raggiunta" }[lvl] || "";
}
function currentRank(plans, id) {
  return plans.findIndex((p) => p.id === id);
}

function Tile({ label, value, tone = "" }) {
  return (
    <div className="rounded-lg bg-dapnow-bg px-2.5 py-2">
      <p className="text-[9px] uppercase font-bold tracking-wider text-dapnow-muted">{label}</p>
      <p className={`text-sm font-extrabold tabular-nums mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}
